import { Injectable, signal } from '@angular/core';
import { interval, Observable, ReplaySubject, Subject } from 'rxjs';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { FiApplicationLanguageType } from '@fi-shared/fi-content-language-shared/fi-application-language-type';
import { comparator } from '@fi-shared/utils/array-sort-comparators';
import { guid } from '@fi-shared/utils/guid';
import { IUserProfile } from '@fi-shared/profile-icon/profile-icon.component';
import { ISiteDesign, ILightWeightObject } from '@fi-shared/models';
import { INameIdObj } from '@fi-shared/fi-shared-interfaces';
import { FiUserSettingType } from '@fi-shared/types/fi-user-setting-type';
import { IReactionFavoriteModel } from '@fi-shared/fi-likes/fi-likes.d';
// for 3rd party embed code developers
declare global {
    interface Window {
        FI_DATA: {
            UserId?: number;
            UserFullName?: string;
            BusinessUnitId?: number;
            BusinessUnitName?: string;
        };
    }
}
window.FI_DATA = {};

export interface IFiUserModuleAccessRights {
    AppSettingsRights: Array<{
        ApplicationSettingType: number;
        AccessRightType: number;
    }>;
    CustomButtons: Array<number>;
}

export enum FiUserSignInType {
    Email,
    PhoneNumber,
    Custom,
}

export interface IFiUserData {
    TokenId: string;
    PreferredContentLanguage: FiApplicationLanguageType;
    PreferredDateFormat: string;
    PreferredTimeFormat: string;
    UserId: number;
    LoginName: string;
    UserEmail: string;
    UserPhone: string;
    WebmailAccountsCount: number;

    ProfilePicture: string;
    ProfilePictureExtension: string;

    ApplicationLanguage: FiApplicationLanguageType;
    FirstName: string;
    FullName: string;
    BusinessUnitName: string;
    BusinessUnitId: number;
    BusinessUnits: ILightWeightObject[];
    //RoleIds: number[];
    //AreaIds: number[];
    IsHeadQuarterUnitMember: boolean;
    ModuleAccessRights: IFiUserModuleAccessRights;
    UserSettings: {
        [prop: number /*FiUserSettingType*/]: number | boolean;
    };
    //SessionExpire: number;
    CanLoginSupportSession: boolean;
    MinDateForNotifications: string; // ISO 8601 datetime
    HasMasterIpAddress: boolean;
    IsSystemObject: boolean;
    //BusinessUnitsCount: number;

    AvailableSiteDesigns: ISiteDesign[];
    ActiveSiteDesign: ISiteDesign;

    SignInUpdateData: {
        IsSendSignInUpdateEmailFailed: boolean;
    };

    ReactionFavorites: IReactionFavoriteModel[];
}

interface IBrowserTab {
    guid: string;
    lastUpdated?: string;
    token?: string;
    closed?: boolean;
}

interface IFiBrowserGlobalData {
    stayLoggedIn?: boolean; //fi-stay-logged-in
    loginRememberMe?: string; //CcmLoginRememberMe
    browserTabs?: IBrowserTab[];
    loggedInUserToken?: string;
}

interface IFiBrowserSessionData {
    guid?: string;
}

const GLOBAL_KEY = 'fi-session-data-global';
const SESSION_KEY = 'fi-session-data';

const UPDATE_INTERVAL = 1000 * 60; // 60 seconds
const REMOVE_NON_EMPTY_TOKENS_INTERVAL = 1000 * 60 * 60 * 24 * 30; // 30 days

@Injectable()
export class FiUserDataService {
    private _userData: IFiUserData;

    private _onUserDataChanged$ = new ReplaySubject<IFiUserData>(1);
    get onUserDataChanged$() {
        return this._onUserDataChanged$.asObservable();
    }

    private _onApplicationLanguageChange$ = new Subject<FiApplicationLanguageType>();
    get onApplicationLanguageChange$() {
        return this._onApplicationLanguageChange$.asObservable();
    }

    private _activeSiteDesignChanged$ = new Subject<ISiteDesign>();
    get activeSiteDesignChanged$() {
        return this._activeSiteDesignChanged$.asObservable();
    }

    get applicationLanguage(): FiApplicationLanguageType {
        if (this.userData) return this.userData.ApplicationLanguage;
        if (this.customerDataService.customerData)
            return this.customerDataService.customerData.UserApplicationLanguageType;
        return FiApplicationLanguageType.English;
    }

    constructor(private customerDataService: FiCustomerDataService) {
        try {
            // caching token part
            localStorage.removeItem('fi-stay-logged-in');
            localStorage.removeItem('CcmLoginRememberMe');
            localStorage.removeItem('fi-logged-users');
            localStorage.removeItem('supportLoggedInUser');
        } catch {}

        const loggedInUserToken = this.getLoggedInUserToken();
        if (loggedInUserToken) {
            this.loggedInUserToken = loggedInUserToken;
            this.removeLoggedInUserToken();
            // preserve loggedInUserToken on refreshing browser's tab
            window.onunload = () => {
                this.setLoggedInUserToken(this.loggedInUserToken);
            };
        } else {
            // update only browser tabs data if it is not support session
            interval(UPDATE_INTERVAL).subscribe(() => {
                if (this.userData) {
                    this.updateCurrentBrowserTabInfo(this.userData.TokenId);
                }
            });

            window.onunload = () => {
                const currentBrowserTabGuid = this.getSessionData().guid;
                this.updateGlobalData((globalData) => {
                    const allBrowserTabs = globalData.browserTabs ?? [];
                    let currentBrowserTab = allBrowserTabs.find((x) => x.guid === currentBrowserTabGuid);
                    if (currentBrowserTab) {
                        currentBrowserTab.closed = true;
                    }
                });
            };
        }
    }

    getUserData(): IFiUserData {
        return this._userData;
    }

    getUserId(): number {
        return this._userData?.UserId;
    }

    getUserFullName(): string {
        return this._userData?.FullName;
    }

    getUserFirstName(): string {
        return this._userData?.FirstName;
    }

    getUserBusinessUnitId(): number {
        return this._userData?.BusinessUnitId;
    }

    private _favoriteReactions = signal<IReactionFavoriteModel[] | undefined>(undefined);
    favoriteReactions = this._favoriteReactions.asReadonly();

    getUserBusinessUnits(): ILightWeightObject[] {
        return this._userData ? this._userData.BusinessUnits : [];
    }

    /*getUserToBusinessUnitIds(): number[] {
        return this.userData
            ? this._userData.BusinessUnits.map(bu => bu.Id)
            : [];
    }*/

    canLoginSupportSession(): boolean {
        return !!this._userData?.CanLoginSupportSession;
    }

    getUserBusinessUnitName(): string {
        return this._userData?.BusinessUnitName;
    }

    getContentLanguage() {
        return this._userData?.PreferredContentLanguage;
    }

    getTokenId() {
        return this._userData?.TokenId;
    }

    getUserProfile(): IUserProfile {
        return this._userData
            ? {
                  Id: this._userData.UserId,
                  FullName: this._userData.FullName,
                  MediaBankPhysicalFileName: this._userData.ProfilePicture,
                  MediaBankExtension: this._userData.ProfilePictureExtension,
              }
            : undefined;
    }

    isUserSystemObject() {
        return this.userData?.IsSystemObject;
    }

    getActiveSiteDesignId() {
        return this._userData.ActiveSiteDesign.Id;
    }

    getAvailableSiteDesigns(): ISiteDesign[] {
        return this.userData ? this._userData.AvailableSiteDesigns : [];
    }

    get userData(): IFiUserData {
        return this.getUserData();
    }

    getToken() {
        if (this.loggedInUserToken) {
            return this.loggedInUserToken;
        }

        if (this.userData) {
            return this.userData.TokenId;
        }

        return this.getCachedToken();
    }

    get authorized() {
        return !!this.getToken();
    }

    setUserData(userData: IFiUserData) {
        if (userData) {
            window.FI_DATA = {
                UserId: userData.UserId,
                UserFullName: userData.FullName,
                BusinessUnitId: userData.BusinessUnitId,
                BusinessUnitName: userData.BusinessUnitName,
            };
        } else {
            window.FI_DATA = {};
            // we should have this at the beginning of method: // https://chainformation.atlassian.net/browse/FI-4853
            this.loggedInUserToken = undefined;
        }

        if (this._userData === userData) return;

        if (this._userData && userData && JSON.stringify(this._userData) === JSON.stringify(userData)) return;

        this._userData = userData;
        this.updateCachedDataOnUserDataChange(this._userData);
        this._onUserDataChanged$.next(this._userData);
        this.updateFavoriteReactions(userData?.ReactionFavorites);
    }

    updateFavoriteReactions(favoriteReactions: IReactionFavoriteModel[]) {
        this._favoriteReactions.set(favoriteReactions);
    }

    updateActiveSiteDesign(newSiteDesign: ISiteDesign) {
        this._userData.ActiveSiteDesign = newSiteDesign;
        this._activeSiteDesignChanged$.next(newSiteDesign);
        //this.onUserDataChange.next(this._userData);
    }

    showSiteDesignAccessForItems() {
        return this._userData?.AvailableSiteDesigns?.length > 1;
    }

    setApplicationLanguage(applicationLanguage: number) {
        this._userData.ApplicationLanguage = applicationLanguage;
        this._onApplicationLanguageChange$.next(applicationLanguage);
    }

    isUserProfileMode(profileUserId: number): boolean {
        return this.getUserData() && this.getUserData().UserId === profileUserId;
    }

    // region Cached data
    getStayLoggedIn() {
        return this.getGlobalData().stayLoggedIn;
    }
    updateStayLoggedIn(newStayLoggedIn: boolean) {
        this.updateGlobalData((globalData) => {
            globalData.stayLoggedIn = newStayLoggedIn;
        });
    }
    getLoginRememberMe() {
        return this.getGlobalData().loginRememberMe;
    }
    updateLoginRememberMe(newLoginRememberMe: string) {
        this.updateGlobalData((globalData) => {
            globalData.loginRememberMe = newLoginRememberMe;
        });
    }

    private getCachedToken() {
        const now = new Date();
        const globalData = this.getGlobalData();
        let browserTabs = (globalData.browserTabs ?? []).slice(0);
        const currentBrowserTabGuid = this.getSessionData().guid;
        let currentBrowserTab = browserTabs.find((x) => x.guid === currentBrowserTabGuid);

        if (currentBrowserTab) return currentBrowserTab.token;

        if (!globalData.stayLoggedIn) {
            browserTabs = browserTabs.filter(
                (x) => !x.closed && now.getTime() - new Date(x.lastUpdated).getTime() <= 2 * UPDATE_INTERVAL,
            );
        }

        browserTabs.sort(comparator((x) => new Date(x.lastUpdated).getTime(), true));
        if (browserTabs.length) {
            return browserTabs[0].token;
        }
    }

    private updateCachedDataOnUserDataChange(newUserData: IFiUserData) {
        if (this.loggedInUserToken) {
            return;
        }
        if (newUserData) {
            this.updateCurrentBrowserTabInfo(newUserData.TokenId);
        } else {
            this.updateGlobalData((globalData) => {
                globalData.browserTabs = [];
                globalData.stayLoggedIn = false;
            });
        }
    }

    updateCurrentBrowserTabInfo(tokenId: string) {
        if (!tokenId || this.loggedInUserToken) {
            return;
        }
        const currentBrowserTabGuid = this.getSessionData().guid;
        this.updateGlobalData((globalData) => {
            const now = new Date();
            const allBrowserTabs = globalData.browserTabs ?? [];

            let currentBrowserTab = allBrowserTabs.find((x) => x.guid == currentBrowserTabGuid);
            if (!currentBrowserTab) {
                currentBrowserTab = {
                    guid: currentBrowserTabGuid,
                };
                allBrowserTabs.push(currentBrowserTab);
            }

            currentBrowserTab.token = tokenId;
            currentBrowserTab.lastUpdated = now.toISOString();
            currentBrowserTab.closed = false;

            // remove old browser tabs
            globalData.browserTabs = allBrowserTabs.filter((browserTab) => {
                if (browserTab.guid === currentBrowserTabGuid) return true;

                const tabLastUpdated = new Date(browserTab.lastUpdated);
                const timeDiffMs = now.getTime() - tabLastUpdated.getTime();

                if (timeDiffMs > REMOVE_NON_EMPTY_TOKENS_INTERVAL) {
                    return false;
                }
                if (!globalData.stayLoggedIn && timeDiffMs > 2 * UPDATE_INTERVAL) {
                    return false;
                }

                return true;
            });
        });
    }

    private updateGlobalData(fn: (data: IFiBrowserGlobalData) => void, forceUpdate?: boolean) {
        if (this.loggedInUserToken && !forceUpdate) {
            return;
        }
        const data = this.getGlobalData();
        fn(data);
        this.saveGlobalData(data);
    }

    private getGlobalData() {
        let data: IFiBrowserGlobalData;
        try {
            data = JSON.parse(localStorage.getItem(GLOBAL_KEY));
        } catch (ex) {
            console.log('getGlobalData err', ex);
        }
        if (!data) {
            data = {};
        }
        return data;
    }

    private saveGlobalData(data: IFiBrowserGlobalData) {
        try {
            localStorage.setItem(GLOBAL_KEY, JSON.stringify(data));
        } catch (ex) {
            console.log('saveGlobalData err', ex);
        }
    }

    private getSessionData() {
        let data: IFiBrowserSessionData;
        try {
            data = JSON.parse(sessionStorage.getItem(SESSION_KEY));
        } catch (ex) {
            console.log('getSessionData err', ex);
        }
        if (!data) {
            data = {};
        }
        if (!data.guid) {
            data.guid = guid();
            this.saveSessionData(data);
        }
        return data;
    }

    private saveSessionData(data: IFiBrowserSessionData) {
        try {
            sessionStorage.setItem(SESSION_KEY, JSON.stringify(data));
        } catch (ex) {
            console.log('saveSessionData err', ex);
        }
    }
    // endregion

    // region Support LoggedIn
    private loggedInUserToken: string;
    private getLoggedInUserToken(): string {
        return this.getGlobalData().loggedInUserToken;
    }
    private removeLoggedInUserToken() {
        this.updateGlobalData((data) => {
            delete data.loggedInUserToken;
        }, true);
    }
    setLoggedInUserToken(loggedInUserToken: string) {
        this.updateGlobalData((data) => {
            data.loggedInUserToken = loggedInUserToken;
        }, true);
    }
    // endregion

    // region change current unit
    setPreferredBusinessUnit(businessUnit: INameIdObj) {
        this.userData.BusinessUnitId = businessUnit.Id;
        this.userData.BusinessUnitName = businessUnit.Name;
        this._onPreferredBusinessUnitChanged$.next(businessUnit);
    }
    private _onPreferredBusinessUnitChanged$ = new Subject<INameIdObj>();
    get onPreferredBusinessUnitChanged$(): Observable<INameIdObj> {
        return this._onPreferredBusinessUnitChanged$.asObservable();
    }
    // endregion
}
